import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Snackbar,
  Badge,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Inventory,
  MoveToInbox,
  Assignment,
  People,
  Person,
  Receipt,
  Assessment,
  Settings,
  Logout,
  PointOfSale,
  Fullscreen,
  FullscreenExit,
  Lock as LockIcon,
  ArrowBackIosNew,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { APP_NAME } from '../constants/app';
import packageJson from '../../package.json';
import logo from '../assets/tienda-logo-alpha.png';
import { usersAPI, apiUtils } from '../services/api';
import UserAvatar from './UserAvatar';
import MobileNavigation from './MobileNavigation';
import useResponsive from '../hooks/useResponsive';

const APP_VERSION = packageJson.version;

const ResponsiveLayout = ({ children, user, onLogout, currentPage, onNavigate }) => {
  const theme = useTheme();
  const { 
    isMobile, 
    isTablet, 
    isDesktop, 
    getDrawerWidth, 
    getSidebarVariant, 
    getAppBarHeight,
    getContentPadding 
  } = useResponsive();

  // State management
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [desktopDrawerOpen, setDesktopDrawerOpen] = useState(true);
  const [drawerMinimized, setDrawerMinimized] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hasOngoingTransaction, setHasOngoingTransaction] = useState(true);
  const [confirmLeaveDialog, setConfirmLeaveDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  
  // Password change dialog state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Notification state
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Responsive drawer width
  const drawerWidth = isMobile ? getDrawerWidth() : (drawerMinimized ? 64 : 240);

  // Styled components
  const AppBarStyled = styled(AppBar, {
    shouldForwardProp: (prop) => prop !== 'open' && prop !== 'ismobile',
  })(({ theme, open, ismobile }) => ({
    zIndex: theme.zIndex.drawer + 1,
    height: getAppBarHeight(),
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && !ismobile && {
      marginLeft: drawerWidth,
      width: `calc(100% - ${drawerWidth}px)`,
      transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    }),
  }));

  const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end',
  }));

  const MainContent = styled('main', {
    shouldForwardProp: (prop) => prop !== 'open' && prop !== 'ismobile',
  })(({ theme, open, ismobile }) => ({
    flexGrow: 1,
    padding: ismobile ? theme.spacing(1) : getContentPadding(),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: ismobile ? 0 : (open ? 0 : `-${drawerWidth}px`),
    marginTop: `${getAppBarHeight()}px`,
    height: `calc(100vh - ${getAppBarHeight()}px)`,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
  }));

  const ContentWrapper = styled(Box)(({ theme }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    '& > *': {
      flexShrink: 0,
    },
    '& > .content-body': {
      flexGrow: 1,
      overflow: 'auto',
      display: 'flex',
      flexDirection: 'column',
    },
  }));

  // Desktop menu items
  const menuItems = [
    {
      text: 'Dashboard',
      icon: <Dashboard />,
      page: 'Dashboard',
    },
    {
      text: 'POS',
      icon: <PointOfSale />,
      page: 'POS',
      badge: hasOngoingTransaction,
    },
    {
      text: 'Products',
      icon: <Inventory />,
      page: 'Products',
    },
    {
      text: 'Product Movements',
      icon: <MoveToInbox />,
      page: 'Product Movements',
    },
    {
      text: 'Inventory Closing',
      icon: <Assignment />,
      page: 'Inventory Closing',
    },
    {
      text: 'Customers',
      icon: <People />,
      page: 'Customers',
    },
    {
      text: 'Users',
      icon: <Person />,
      page: 'Users',
    },
    {
      text: 'Transactions',
      icon: <Receipt />,
      page: 'Transactions',
    },
    {
      text: 'Reports',
      icon: <Assessment />,
      page: 'Reports',
    },
    {
      text: 'Settings',
      icon: <Settings />,
      page: 'Settings',
    },
  ];

  // Event handlers
  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileDrawerOpen(!mobileDrawerOpen);
    } else {
      if (desktopDrawerOpen && !drawerMinimized) {
        setDrawerMinimized(true);
      } else if (desktopDrawerOpen && drawerMinimized) {
        setDesktopDrawerOpen(false);
        setDrawerMinimized(false);
      } else {
        setDesktopDrawerOpen(true);
      }
    }
  };

  const handleMobileDrawerClose = () => {
    setMobileDrawerOpen(false);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    onLogout();
  };

  const handleChangePassword = () => {
    setPasswordDialogOpen(true);
    handleProfileMenuClose();
  };

  const handlePasswordDialogClose = () => {
    setPasswordDialogOpen(false);
    setPasswordData({
      current_password: '',
      new_password: '',
      confirm_password: ''
    });
    setPasswordError('');
  };

  const handlePasswordInputChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
    setPasswordError('');
  };

  const handlePasswordSubmit = async () => {
    if (!passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password) {
      setPasswordError('Please fill in all fields');
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 6) {
      setPasswordError('New password must be at least 6 characters long');
      return;
    }

    setPasswordLoading(true);
    try {
      await usersAPI.updatePassword(user.id, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      
      handlePasswordDialogClose();
      showNotification('Password changed successfully!', 'success');
    } catch (error) {
      const errorMessage = apiUtils.handleError(error);
      setPasswordError(errorMessage);
    } finally {
      setPasswordLoading(false);
    }
  };

  const showNotification = (message, severity = 'success') => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  const handleNotificationClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setNotification(prev => ({ ...prev, open: false }));
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      document.documentElement.requestFullscreen();
    } else {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      }
    }
  };

  // Fullscreen event listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Desktop drawer content
  const desktopDrawerContent = (
    <Box>
      <DrawerHeader>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          {!drawerMinimized && (
            <>
              <img
                src={logo}
                alt="Logo"
                style={{ height: 32, width: 'auto', marginRight: 8 }}
              />
              <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
                {user?.entity_name || APP_NAME}
              </Typography>
            </>
          )}
        </Box>
        <IconButton onClick={() => setDrawerMinimized(!drawerMinimized)}>
          {drawerMinimized ? <ChevronRight /> : <ChevronLeft />}
        </IconButton>
      </DrawerHeader>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem
            key={item.text}
            button
            onClick={() => onNavigate(item.page)}
            sx={{
              minHeight: 48,
              justifyContent: drawerMinimized ? 'center' : 'initial',
              px: 2.5,
              backgroundColor: currentPage.toLowerCase() === item.page.toLowerCase() 
                ? 'action.selected' 
                : 'transparent',
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: drawerMinimized ? 'auto' : 3,
                justifyContent: 'center',
                color: currentPage.toLowerCase() === item.page.toLowerCase() 
                  ? 'primary.main' 
                  : 'inherit',
              }}
            >
              {item.badge ? (
                <Badge 
                  color="error" 
                  variant="dot" 
                  invisible={!item.badge}
                >
                  {item.icon}
                </Badge>
              ) : (
                item.icon
              )}
            </ListItemIcon>
            {!drawerMinimized && (
              <ListItemText
                primary={item.text}
                sx={{
                  '& .MuiListItemText-primary': {
                    color: currentPage.toLowerCase() === item.page.toLowerCase() 
                      ? 'primary.main' 
                      : 'inherit',
                    fontWeight: currentPage.toLowerCase() === item.page.toLowerCase() 
                      ? 600 
                      : 400,
                  }
                }}
              />
            )}
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* App Bar */}
      <AppBarStyled 
        position="fixed" 
        open={desktopDrawerOpen && !isMobile}
        ismobile={isMobile}
      >
        <Toolbar sx={{ minHeight: `${getAppBarHeight()}px !important` }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerToggle}
            edge="start"
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {JSON.parse(localStorage.getItem('user'))?.entity_name || APP_NAME} | {currentPage}
          </Typography>

          {/* Desktop only: Fullscreen toggle */}
          {!isMobile && (
            <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}>
              <IconButton
                size="large"
                aria-label="toggle fullscreen"
                color="inherit"
                onClick={toggleFullscreen}
                sx={{ mr: 1 }}
              >
                {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
              </IconButton>
            </Tooltip>
          )}

          {/* Profile Button */}
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleProfileMenuOpen}
            color="inherit"
          >
            <UserAvatar 
              seed={user?.email || user?.username || 'default'} 
              size={isMobile ? 32 : 40}
              style="thumbs"
              sx={{ 
                border: '2px solid rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  border: '2px solid rgba(255, 255, 255, 0.4)',
                }
              }}
            />
          </IconButton>

          {/* Profile Menu */}
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
          >
            <MenuItem onClick={handleProfileMenuClose}>
              <Typography textAlign="center">{user?.username}</Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleChangePassword}>
              <LockIcon sx={{ mr: 1 }} /> Change Password
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <Logout sx={{ mr: 1 }} /> Logout
            </MenuItem>
            <Divider />
            <MenuItem disabled sx={{ justifyContent: 'center' }}>
              <Typography variant="caption" color="text.secondary">v{APP_VERSION}</Typography>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBarStyled>
      
      {/* Desktop Drawer */}
      {!isMobile && (
        <Drawer
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              backgroundColor: 'background.paper',
              overflowX: 'hidden',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          }}
          variant="persistent"
          anchor="left"
          open={desktopDrawerOpen}
        >
          {desktopDrawerContent}
        </Drawer>
      )}

      {/* Mobile Navigation */}
      {isMobile && (
        <MobileNavigation
          user={user}
          currentPage={currentPage}
          onNavigate={onNavigate}
          onLogout={onLogout}
          drawerOpen={mobileDrawerOpen}
          onDrawerClose={handleMobileDrawerClose}
          onProfileMenuOpen={handleProfileMenuOpen}
        />
      )}

      {/* Main Content */}
      <MainContent 
        open={desktopDrawerOpen && !isMobile}
        ismobile={isMobile}
      >
        <ContentWrapper>
          {children}
        </ContentWrapper>
      </MainContent>

      {/* Password Change Dialog */}
      <Dialog 
        open={passwordDialogOpen} 
        onClose={handlePasswordDialogClose}
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              onClick={handlePasswordDialogClose}
              sx={{ mr: 2 }}
            >
              <ArrowBackIosNew />
            </IconButton>
          )}
          Change Password
        </DialogTitle>
        <DialogContent>
          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordError}
            </Alert>
          )}
          
          <TextField
            name="current_password"
            label="Current Password"
            type="password"
            value={passwordData.current_password}
            onChange={handlePasswordInputChange}
            fullWidth
            required
            margin="normal"
            disabled={passwordLoading}
          />
          
          <TextField
            name="new_password"
            label="New Password"
            type="password"
            value={passwordData.new_password}
            onChange={handlePasswordInputChange}
            fullWidth
            required
            margin="normal"
            disabled={passwordLoading}
            helperText="Password must be at least 6 characters long"
          />
          
          <TextField
            name="confirm_password"
            label="Confirm New Password"
            type="password"
            value={passwordData.confirm_password}
            onChange={handlePasswordInputChange}
            fullWidth
            required
            margin="normal"
            disabled={passwordLoading}
          />
        </DialogContent>
        <DialogActions sx={{ p: isMobile ? 2 : 1 }}>
          <Button 
            onClick={handlePasswordDialogClose} 
            disabled={passwordLoading}
            fullWidth={isMobile}
          >
            Cancel
          </Button>
          <Button 
            onClick={handlePasswordSubmit} 
            variant="contained" 
            disabled={passwordLoading}
            fullWidth={isMobile}
            sx={{ ml: isMobile ? 0 : 1, mt: isMobile ? 1 : 0 }}
          >
            {passwordLoading ? 'Changing...' : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={handleNotificationClose}
        anchorOrigin={{ 
          vertical: isMobile ? 'top' : 'bottom', 
          horizontal: isMobile ? 'center' : 'right' 
        }}
      >
        <Alert
          onClose={handleNotificationClose}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ResponsiveLayout;